import React, { useState, useEffect, useRef } from "react";
import "./CheckoutForm.scss";

import LilPipSmiling from "../../assets/images/svg/lilPipSmiling.svg";
import { createDonationCheckoutUrl } from "../../utilities/api";
import { useLocale } from "../../i18n/LocaleContext";

const Field = ({
  label,
  id,
  type,
  placeholder,
  required,
  autoComplete,
  value,
  onChange,
}) => (
  <div className="FormRow">
    <label htmlFor={id} className="FormRowLabel">
      {label}
    </label>
    <input
      className="FormRowInput"
      id={id}
      type={type}
      placeholder={placeholder}
      required={required}
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
      onKeyPress={(e) => {
        e.key === "Enter" && e.preventDefault();
      }}
    />
  </div>
);

const ErrorMessage = ({ children }) => (
  <div className="ErrorMessage" role="alert">
    {children}
  </div>
);

const CheckoutForm = (props) => {
  const { t } = useLocale();
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [donorName, setDonorName] = useState("");
  const [email, setEmail] = useState("");
  const [donationAmount, setDonationAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState(0);
  const [randomNamePlaceholder, setRandomNamePlaceholder] = useState("");
  const amountTooHighError = t("donation.amountTooHigh");

  const customAmountInput = useRef(null);

  useEffect(() => {
    if (!props.showing) {
      return;
    }
    setSucceeded(false);
    setError(null);
    setProcessing(false);
    setDonorName("");
    setEmail("");
    setDonationAmount(0);
    setCustomAmount(0);

    const parsedSuccessAmount = Number(props.successAmount);
    if (Number.isFinite(parsedSuccessAmount) && parsedSuccessAmount > 0) {
      setSucceeded(true);
      setDonationAmount(parsedSuccessAmount);
    }

    setRandomNamePlaceholder(() => {
      const names = [
        "Albert Einstein",
        "Marie Curie",
        "Isaac Newton",
        "Michael Faraday",
        "Emmy Noether",
        "Carl Sagan",
        "Max Planck",
        "Cecilia Payne",
      ];
      return names[Math.floor(Math.random() * names.length)];
    });
  }, [props.showing, props.successAmount]);

  const AmountButton = ({ processing, error, amount }) => (
    <button
      className={`AmountButton ${
        amount === donationAmount ? "AmountButtonSelected" : ""
      } ${error ? "AmountButton--error" : ""}`}
      disabled={processing}
      onClick={(e) => {
        e.preventDefault();
        if (error === amountTooHighError) {
          setError(null);
        }
        setDonationAmount((prev) => {
          setCustomAmount(0);
          if (prev === amount) return 0;
          return amount;
        });
      }}
    >
      ${amount}
    </button>
  );

  const handleCustomAmountChange = (e) => {
    e.preventDefault();
    if (e.target.validity.valid) {
      if (e.target.value < 1000000) {
        setCustomAmount(e.target.value);
        setDonationAmount(e.target.value);

        if (error === amountTooHighError) {
          setError(null);
        }
      } else {
        setError(amountTooHighError);
      }
    }
  };

  const SubmitButton = ({
    processing,
    error,
    children,
    disabled,
  }) => (
    <button
      className={`SubmitButton ${processing ? "SubmitButtonProcessing" : ""} ${
        error ? "SubmitButton--error" : ""
      }`}
      type="submit"
      disabled={processing || disabled}
    >
      {processing ? t("donation.processing") : children}
    </button>
  );

  const handleSubmit = async (ev) => {
    ev.preventDefault();

    if (donationAmount < 1) {
      setError(t("donation.selectAmount"));
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const response = await window.fetch(createDonationCheckoutUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [{ id: { donationAmount } }],
          email,
          name: donorName,
          returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error ?? t("donation.errorStartCheckout"));
      }

      if (!data?.url) {
        throw new Error(t("donation.errorStartCheckout"));
      }

      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : t("donation.errorStartCheckout"));
      setProcessing(false);
    }
  };

  return succeeded ? (
    <div className="ResultGroup">
      <img className="ResultImageHeader" src={LilPipSmiling} />
      <div className="Result">
        <div className="ResultTitle" role="alert">
          {t("donation.successTitle")}
        </div>
        <div className="ResultMessage">
          {t("donation.successMessage", { amount: donationAmount })}
        </div>
      </div>
    </div>
  ) : (
    <form className="Form" onSubmit={handleSubmit}>
      <h1 className="CheckoutHeader">
        {t("donation.header")}
      </h1>
      <p className="CheckoutSubheader">{t("donation.subheader")}</p>
      <fieldset className="FormGroup">
        <Field
          label={t("auth.name")}
          id="name"
          type="text"
          placeholder={randomNamePlaceholder}
          required
          autoComplete="name"
          value={donorName}
          onChange={(e) => {
            setDonorName(e.target.value);
          }}
        />
        <Field
          label={t("auth.email")}
          id="email"
          type="email"
          placeholder={t("donation.emailPlaceholder")}
          required
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />
      </fieldset>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <div className="AmountButtons">
        <AmountButton
          processing={processing}
          error={error}
          amount={5}
        />
        <AmountButton
          processing={processing}
          error={error}
          amount={20}
        />
        <AmountButton
          processing={processing}
          error={error}
          amount={50}
        />
        <AmountButton
          processing={processing}
          error={error}
          amount={100}
        />
        <AmountButton
          processing={processing}
          error={error}
          amount={250}
        />
        <button
          className={`CustomAmountButton ${
            customAmount > 0 ? "CustomAmountButtonSelected" : ""
          } ${error ? "CustomAmountButton--error" : ""}`}
          disabled={processing}
          onClick={(e) => {
            e.preventDefault();
            if (customAmount === 0) {
              setDonationAmount(0);
            }
            customAmountInput.current.focus();
          }}
        >
          $
          <input
            type="text"
            ref={customAmountInput}
            pattern="[0-9]*"
            value={customAmount > 0 ? customAmount : ""}
            onChange={handleCustomAmountChange}
            className={"CustomAmountTextField"}
            onKeyPress={(e) => {
              e.key === "Enter" && e.preventDefault();
            }}
          ></input>
        </button>
      </div>
      <SubmitButton
        processing={processing}
        error={error}
        disabled={donationAmount < 1}
      >
        {donationAmount < 1
          ? t("donation.selectAmount")
          : t("donation.donateAmount", { amount: donationAmount })}
      </SubmitButton>
    </form>
  );
};

export default CheckoutForm;
